import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { EmailFormat, projectKeyType, DataState, RuleSetId } from '../../common/types';
import type { InitialData, StylingData } from '../../common/types/send-me-a-test-email';
import type { SendTestMailResponse } from './types';

export const useSendMeATestEmailService = (
	projectKey: projectKeyType,
	format: EmailFormat,
	initialContent: InitialData,
	stylingData: StylingData,
	ruleSetId?: RuleSetId,
): DataState<SendTestMailResponse> & {
	sendTestEmail: () => Promise<SendTestMailResponse | undefined>;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const postRequest = async () => {
		let resp;
		try {
			if (ruleSetId) {
				resp = await performPostRequest<SendTestMailResponse>(
					'/rest/servicedesk/notifications/1/content-preview/ruleset/send-email',
					{
						body: JSON.stringify({
							languageTag: initialContent.languageTag,
							projectKey,
							ruleSetId,
							format,
							contentTemplate: stylingData.textTemplate,
						}),
					},
				);
			} else {
				resp = await performPostRequest<SendTestMailResponse>(
					'/rest/servicedesk/notifications/1/content-preview/send-email',
					{
						body: JSON.stringify({
							languageTag: initialContent.languageTag,
							projectKey,
							format,
							contexts: initialContent.contexts,
							stylingData: {
								subjectTemplate: stylingData.subjectTemplate,
								htmlTemplate: stylingData.htmlTemplate,
								textTemplate: stylingData.textTemplate,
								css: stylingData.css,
							},
						}),
					},
				);
			}

			return resp;
		} catch (error) {
			fireErrorAnalytics({
				event: createAnalyticsEvent({
					action: 'sendTestEmail',
					actionSubject: 'failed',
				}),
				error: error instanceof Error ? error : undefined,
				meta: {
					teamName: 'jsm-nimbus',
					packageName: 'jiraLanguageSettings',
					id: 'useSendMeATestEmailService',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		}
	};

	const {
		loading,
		error,
		data,
		fetch: sendTestEmail,
	} = useService<SendTestMailResponse>(postRequest);
	return { data, error, loading, sendTestEmail };
};
